/**
 * The short date the copy rule asks for ("Mon Oct 26"), moved byte-identically out of
 * `pages/Portal/ActionPlan/actionPlan.ts` (which re-exports it) so the matter rows can print
 * it without pulling that file, and its board palette, into the frozen Filings closure.
 */
export const utc = (iso: string): number => {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return Date.UTC(y, (m || 1) - 1, d || 1);
};

/** One Intl.DateTimeFormat per options: toLocaleDateString builds one a call, 118 ms of a 6x CPU pick (O8 F5). */
const FORMATS = new Map<string, Intl.DateTimeFormat>();
export const fmt = (iso: string, opts: Intl.DateTimeFormatOptions): string => {
  const k = JSON.stringify(opts);
  let f = FORMATS.get(k);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { ...opts, timeZone: "UTC" });
    FORMATS.set(k, f);
  }
  return f.format(utc(iso));
};

/** Tooltip sentences: "Fri Oct 16", the year only when it is not today's. */
export const formatDay = (iso: string, today: string): string =>
  `${fmt(iso, { weekday: "short" })} ${fmt(iso, { month: "short", day: "numeric" })}${
    iso.slice(0, 4) === today.slice(0, 4) ? "" : `, ${iso.slice(0, 4)}`
  }`;

export const DAY_MS = 86_400_000;

/** Whole days from `fromIso` to `iso`, both server New York dates. */
export const daysBetween = (fromIso: string, iso: string): number =>
  Math.round((utc(iso) - utc(fromIso)) / DAY_MS);

/** Board R6: "Today", "Tue Oct 6" inside 90 days, "Apr 6, 2027" beyond. */
export const formatActBy = (iso: string, today: string): string => {
  if (iso === today) return "Today";
  if (Math.abs(daysBetween(today, iso)) <= 90) return formatDay(iso, iso);
  return fmt(iso, { month: "short", day: "numeric", year: "numeric" });
};

/** A date mid-sentence ("by Fri Jan 8"), "today" on the day; one convention for the bar tips and the rail. */
export const rowDay = (iso: string, today: string) =>
  iso === today ? "today" : formatActBy(iso, today);

const STAMP = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  // NOT `hour12: false`: V8 resolves it to h24, so 2026-11-01T04:05Z read "24:05 EDT" (Q22).
  hourCycle: "h23",
  timeZoneName: "short",
});

/**
 * "Sep 23, 2026 12:49 EDT": an instant in the reader's zone, with the zone printed (Leo 09-23). Every portal
 * "as of" prints `money_as_of` through this or `formatStampDay` (Q22, Leo 10-31). `null` for nullish or
 * unparseable input.
 */
export const formatUtcStamp = (
  iso: string | null | undefined,
): string | null => {
  if (!iso) return null;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  const parts = STAMP.formatToParts(t);
  const at = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${at("month")} ${at("day")}, ${at("year")} ${at("hour")}:${at("minute")} ${at("timeZoneName")}`;
};

/** "YYYY-MM-DD" of an instant on the reader's own calendar. */
const localDay = (t: Date): string =>
  `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;

/** "Fri Oct 30": the reader's day of an instant (never the UTC day), as `formatDay` prints it. */
export const formatStampDay = (
  iso: string | null | undefined,
  now: Date = new Date(),
): string | null => {
  if (!iso) return null;
  const t = new Date(iso);
  return Number.isNaN(t.getTime())
    ? null
    : formatDay(localDay(t), localDay(now));
};
